/**
 * chatCapture.js — thin API client for the 8 chat capture endpoints.
 *
 * All functions return the parsed JSON response body on success. On non-2xx,
 * they throw an Error with a `.status` property and `.detail` (response body
 * text or parsed `detail` field). Callers inspect `.status` to distinguish
 * 409 (race / already-captured), 404 (cross-tenant or deleted), 400 (bad
 * cursor / bad predicate), etc.
 *
 * Auth: reads the JWT from localStorage('authToken') and sets Authorization
 * header. The global 401 interceptor in authInterceptor.js handles session
 * expiry — we do not branch on 401 here.
 *
 * No retry logic. Callers decide retry policy.
 */

import { API_BASE } from '../config';


function authHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


async function request(method, path, body) {
  const headers = { ...authHeader() };
  const init = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, init);

  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data?.detail || JSON.stringify(data);
    } catch (_) {
      try {
        detail = await res.text();
      } catch (__) {
        detail = '';
      }
    }
    const err = new Error(
      `chatCapture ${method} ${path} failed: ${res.status} ${detail}`
    );
    err.status = res.status;
    err.detail = detail;
    throw err;
  }

  // 204 No Content is possible on some lifecycle endpoints — parse defensively.
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}


// ============================================================================
// Lifecycle endpoints (per-message)
// ============================================================================

export function evaluateMessage(rawText, chatMessageId = null, opts = {}) {
  // opts: { assistantResponse, conversationId, clientTurnId, derivedFactResponse } — optional,
  // added for the chat-logging export. A deterministic derived fact must never
  // enter the capture/deck evidence channel.
  // chat-logging export. The backend EvaluateRequest accepts assistant_response
  // and conversation_id as nullable fields; omitting them is fully backwards
  // compatible (older callers and tests that pass only rawText still work).
  const {
    assistantResponse = null,
    conversationId = null,
    clientTurnId = null,
    derivedFactResponse = false,
    // Which chat the turn came from (home, quoting, sourcing, deck), for the
    // admin chat log (Lane 1391). Omitted: the server records home.
    chatSurface = null,
  } = opts;
  return request('POST', '/api/chat/capture/evaluate', {
    raw_text: rawText,
    chat_message_id: chatMessageId,
    assistant_response: assistantResponse,
    conversation_id: conversationId,
    client_turn_id: clientTurnId,
    derived_fact_response: derivedFactResponse,
    ...(chatSurface ? { chat_surface: chatSurface } : {}),
  });
}

export function confirmCandidate(candidateId) {
  return request('POST', `/api/chat/capture/confirm/${candidateId}`);
}

export function editCandidate(candidateId, edits) {
  // edits: { entity_name?, predicate?, value? } — backend fills through to
  // existing values for any field omitted.
  return request('POST', `/api/chat/capture/edit/${candidateId}`, edits);
}

export function retractCandidate(candidateId) {
  return request('POST', `/api/chat/capture/retract/${candidateId}`);
}

export function dismissCandidate(candidateId) {
  return request('POST', `/api/chat/capture/dismiss/${candidateId}`);
}


// ============================================================================
// List endpoints (user + admin)
// ============================================================================

export function listCaptures({ cursor = null, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (cursor != null) params.set('cursor', String(cursor));
  params.set('limit', String(limit));
  return request('GET', `/api/chat/capture/candidates?${params.toString()}`);
}

export function listAdminCaptures({ companyId, status = null, cursor = null, limit = 50 }) {
  const params = new URLSearchParams();
  params.set('company_id', String(companyId));
  if (status) params.set('status', status);
  if (cursor != null) params.set('cursor', String(cursor));
  params.set('limit', String(limit));
  return request('GET', `/api/admin/chat/capture/candidates?${params.toString()}`);
}


// ============================================================================
// Admin — per-company kill switch toggle
// ============================================================================

export function setKillSwitch(companyId, enabled) {
  return request('PUT', `/api/admin/companies/${companyId}/chat-capture`, {
    enabled: Boolean(enabled),
  });
}
